import { ReactNode } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import {
    isRestoreUrl,
    TableRestore,
    controlActivitiesListColumnFiltersCodecMap,
    controlActivitiesListDataParamsCodecMap,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import { useLocation, useParams } from "react-router-dom";

import {
    controlIdParamSchema,
    controlProgramIdParamSchema,
} from "org/controlActivities/urlUtils";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";

import {
    ALL_ACTIVITIES_SCOPE,
    buildControlActivitiesListDefaultState,
    buildControlActivitiesListTableStateStorageKey,
    ControlActivitiesScope,
} from "./consts";
import { getActiveCategoryNames } from "./utils/category";

function useRouteScope(
    scopeType: ControlActivitiesScope["type"]
): ControlActivitiesScope | null {
    const { id } = useParams();
    switch (scopeType) {
        case "all":
            return ALL_ACTIVITIES_SCOPE;
        case "controlProgram": {
            const result = controlProgramIdParamSchema.safeParse(id);
            return result.success
                ? { type: "controlProgram", controlProgramId: result.data }
                : null;
        }
        case "control": {
            const result = controlIdParamSchema.safeParse(id);
            return result.success
                ? { type: "control", controlId: result.data }
                : null;
        }
        default:
            return assertNever(scopeType);
    }
}

interface ControlActivitiesRestoreProps {
    scopeType: ControlActivitiesScope["type"];
    children: ReactNode;
}

export function ControlActivitiesRestore({
    scopeType,
    children,
}: ControlActivitiesRestoreProps) {
    const scope = useRouteScope(scopeType);
    const { search } = useLocation();

    if (scope === null || !isRestoreUrl(new URLSearchParams(search))) {
        return children;
    }

    return (
        <ControlActivitiesTableRestore scope={scope}>
            {children}
        </ControlActivitiesTableRestore>
    );
}

interface ControlActivitiesTableRestoreProps {
    scope: ControlActivitiesScope;
    children: ReactNode;
}

function ControlActivitiesTableRestore({
    scope,
    children,
}: ControlActivitiesTableRestoreProps) {
    const { data: tags } = useTagsSuspenseQuery();

    return (
        <TableRestore
            config={{
                tableId: buildControlActivitiesListTableStateStorageKey(scope),
                defaultValues: buildControlActivitiesListDefaultState(
                    getActiveCategoryNames(tags.tagCategories),
                    scope
                ),
                codecMap: controlActivitiesListColumnFiltersCodecMap,
                onError: Sentry.captureException,
                dataParamsCodecMap: controlActivitiesListDataParamsCodecMap,
            }}
        >
            {children}
        </TableRestore>
    );
}
